import React, { useState, useMemo } from 'react';
import PropTypes from 'prop-types';
import { useTranslation } from 'react-i18next';
import TextField from '@material-ui/core/TextField';
import Autocomplete from '@material-ui/lab/Autocomplete';
import { matchSorter } from 'match-sorter';

// STYLES
import { AutocompleteWrapper, ListItem, ChevronIcon, AdornmentWrapper, NoResults } from './styles';

const MultiselectSearch = ({
  name,
  placeholder,
  data,
  selected: selectedData,
  onChange,
  InputProps,
  showChevron,
  ...restProps
}) => {
  const [t] = useTranslation();
  const [open, setOpen] = useState(false);
  const [searchString, setSearchString] = useState('');

  const handleClose = (e, reason) => {
    if (e?.keyCode === 13 && (reason === 'reset' || reason === 'create-option')) {
      return;
    }

    setOpen(false);
    setSearchString('');
  };

  const handleListItem = (event, value) => {
    event.stopPropagation();
    let reason = event.target?.dataset?.reason;

    if (!reason) {
      const optionId = event.target.getAttribute('aria-activedescendant');
      const optionElement = document.getElementById(optionId);

      // CHECK FOR EXISTING OPTIONELEMENT
      if (!optionElement) {
        return;
      }

      const optionElementChildNode = optionElement.childNodes[0];
      reason = optionElementChildNode?.dataset?.reason;
    }
    const array = [...selectedData];

    if (reason === 'remove-option') {
      const selectedIndex = selectedData.indexOf(value.id);
      array.splice(selectedIndex, 1);
    }

    if (reason === 'select-option') {
      array.push(value.id);
      array.sort((a, b) => {
        return a - b;
      });
    }

    onChange(name, array);
  };

  const renderEndAdornment = useMemo(() => {
    let chevron = null;
    let classes = 'adornment';

    if (showChevron) {
      chevron = <ChevronIcon className={open ? 'is-open' : ''} />;
    }

    if (restProps.size === 'small') {
      classes += ' adornment--small';
    }

    // has content check
    if (InputProps.endAdornment || chevron) {
      return (
        <AdornmentWrapper className={classes}>
          {InputProps.endAdornment}
          {chevron}
        </AdornmentWrapper>
      );
    }

    return null;
  }, [open, selectedData]);

  const filterOptions = (options, { inputValue }) => {
    return matchSorter(options, inputValue, { keys: [item => item.name?.toUpperCase()] });
  };

  const onInputChange = (event, value, reason) => {
    if (reason === 'reset') {
      return;
    }

    if (event?.keyCode === 13 && reason === 'reset') {
      return;
    }

    let newInputValue = value;
    if (reason === 'reset' || reason === 'clear') {
      newInputValue = '';
    }

    setSearchString(newInputValue);
  };

  return (
    <AutocompleteWrapper>
      <Autocomplete
        open={open}
        {...restProps}
        freeSolo
        inputValue={searchString}
        onOpen={() => setOpen(true)}
        onClose={handleClose}
        onChange={handleListItem}
        onInputChange={onInputChange}
        options={data}
        filterOptions={filterOptions}
        getOptionLabel={option => option.title || ''}
        ListboxComponent="ul"
        forcePopupIcon={false}
        value=""
        openOnFocus
        disableClearable
        disablePortal
        disableCloseOnSelect
        renderOption={option => {
          const selected = selectedData.includes(option.id);

          return (
            <ListItem
              data-reason={selected ? 'remove-option' : 'select-option'}
              selected={selected}
              have={option.list_status}
              key={`${name}-option-${option.id}-${option.name}`}
            >
              <i className="icon-check" /> {option.name}
            </ListItem>
          );
        }}
        renderInput={params => {
          return (
            <TextField
              {...params}
              placeholder={placeholder}
              InputProps={{
                ...params.InputProps,
                endAdornment: renderEndAdornment,
              }}
            />
          );
        }}
        noOptionsText={<NoResults>{t('searchAutoComplete.noResults')}</NoResults>}
      />
    </AutocompleteWrapper>
  );
};

MultiselectSearch.defaultProps = {
  data: [],
  selected: [],
  InputProps: { endAdornment: null },
  showChevron: false,
};

MultiselectSearch.propTypes = {
  data: PropTypes.array,
  selected: PropTypes.array,
  name: PropTypes.string,
  placeholder: PropTypes.string,
  onChange: PropTypes.func,
  InputProps: PropTypes.object,
  showChevron: PropTypes.bool,
};

export default React.memo(MultiselectSearch);
